import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Bot, Send, Sparkles } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";

interface AIChatModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AIChatModal = ({ open, onOpenChange }: AIChatModalProps) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-2">
            <Bot className="h-6 w-6 text-primary" />
            PEO AI Assistant
          </DialogTitle>
          <DialogDescription className="text-base">
            Get instant answers about PEOs, our platform, and personalized recommendations for your business.
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex-1 flex flex-col gap-4 mt-4">
          {/* Sample Questions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                Try asking me:
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {[
                  "What is a PEO and how does it work?",
                  "How much does a PEO typically cost?",
                  "What are the benefits of using a PEO?",
                  "How do I choose the right PEO for my business?",
                  "What's included in PEO services?",
                  "How long does PEO implementation take?"
                ].map((question, index) => (
                  <Button
                    key={index}
                    variant="ghost"
                    className="h-auto text-left justify-start p-3 text-sm"
                    onClick={() => {/* TODO: Handle question click */}}
                  >
                    "{question}"
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Chat Area */}
          <div className="flex-1 border rounded-lg">
            <ScrollArea className="h-full p-4">
              <div className="space-y-4">
                {/* Welcome Message */}
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Bot className="h-4 w-4 text-primary" />
                  </div>
                  <div className="bg-muted p-3 rounded-lg max-w-sm">
                    <p className="text-sm">
                      👋 Hi! I'm your PEO AI assistant. I can help you understand PEOs, 
                      compare options, and guide you through our platform. What would you like to know?
                    </p>
                  </div>
                </div>
              </div>
            </ScrollArea>
          </div>

          {/* Chat Input */}
          <div className="flex gap-2 p-4 border-t">
            <Input 
              placeholder="Ask me anything about PEOs..." 
              className="flex-1"
              disabled
            />
            <Button disabled>
              <Send className="h-4 w-4" />
            </Button>
          </div>

          <div className="bg-muted/50 p-3 rounded-lg">
            <p className="text-sm text-center text-muted-foreground">
              🤖 <strong>Coming Soon:</strong> Our AI assistant is currently in development. 
              In the meantime, feel free to explore our application or schedule a consultation with our experts.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};